import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LeadForm from "@/components/common/LeadForm";
import { cityConfig } from "@/config/cityConfig";
import { useMeta, useSchema } from "@/hooks/useMeta";

export default function SeoPage({ config }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: config.h1,
    provider: {
      "@type": "RealEstateAgent",
      name: "BRIMO Immobilien NRW",
      telephone: "+4921293766829",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Wilhelmstraße 3",
        addressLocality: "Haan",
        postalCode: "42781",
        addressCountry: "DE",
      },
    },
    areaServed: "Kreis Mettmann",
    description: config.description,
  };

  useMeta(config.title, config.description);
  useSchema(schema);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [config.h1]);

  const isSeller = config.lead_type === "seller" || config.lead_type === "market_value";

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-end pb-16" data-testid="seo-hero">
        <div className="absolute inset-0">
          <img
            src={config.heroImage}
            alt={config.h1}
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/90" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl pt-28">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-5" />
          <nav className="flex items-center gap-2 text-gray-500 text-xs mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#E61D38] transition-colors">Start</Link>
            <span>/</span>
            <span className="text-gray-400">{config.h1}</span>
          </nav>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-none mb-5"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            {config.h1.toUpperCase()}
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl leading-relaxed mb-8">{config.intro}</p>
          <Link
            to={isSeller ? "/marktwertermittlung-kreis-mettmann" : "/immobilie-kaufen-kreis-mettmann"}
            className="flex items-center gap-2 bg-[#E61D38] text-white px-7 py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#c41930] transition-colors w-fit"
            data-testid="seo-hero-cta"
          >
            {config.formCta}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="seo-content">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {config.sections?.map((section, i) => (
                <div key={i} className="mb-12" data-testid={`seo-section-${i}`}>
                  <div className="h-0.5 w-8 bg-[#E61D38] mb-4" />
                  <h2
                    className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight mb-5"
                    style={{ fontFamily: "Montserrat, sans-serif" }}
                  >
                    {section.title.toUpperCase()}
                  </h2>
                  <p className="text-gray-400 leading-relaxed text-sm">{section.content}</p>
                </div>
              ))}

              {/* Why BRIMO */}
              <div className="bg-[#262626] border border-white/5 p-8 mt-8">
                <h3
                  className="text-white font-bold text-sm uppercase tracking-wider mb-6"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Warum BRIMO Immobilien NRW?
                </h3>
                <ul className="space-y-3">
                  {[
                    "Lokale Marktkenntnis in allen Städten im Kreis Mettmann",
                    "Kostenlose Erstberatung ohne Verpflichtung",
                    "Persönlicher Ansprechpartner vom ersten Gespräch bis zum Abschluss",
                    "Professionelle Vermarktung auf allen relevanten Plattformen",
                    "Strukturierter Prozess für optimale Ergebnisse",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle size={14} className="text-[#E61D38] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Districts Link */}
              <div className="mt-10">
                <h3
                  className="text-white font-bold text-sm uppercase tracking-wider mb-5"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Wir sind aktiv im gesamten Kreis Mettmann
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {cityConfig.districts.map((d) => (
                    <Link
                      key={d.slug}
                      to={`/immobilie-verkaufen-${d.slug}`}
                      className="flex items-center gap-2 text-gray-400 text-sm hover:text-[#E61D38] transition-colors bg-[#262626] px-4 py-3 border border-white/5 hover:border-[#E61D38]/30"
                      data-testid={`seo-district-${d.slug}`}
                    >
                      <ArrowRight size={12} className="text-[#E61D38]" />
                      {d.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar Form */}
            <div>
              <div className="sticky top-24">
                <LeadForm
                  formType={config.lead_type}
                  title={config.formTitle}
                  ctaText={config.formCta}
                  showDistrict={config.showDistrict}
                  showBudget={config.showBudget}
                  showPropertyType={true}
                  pageSource={`/${Object.keys(config).length}`}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
